/**
 * Journey dispatcher (ADR-0159 §3, §6) — the sole public entry to the
 * journey system. Every per-protocol module is internal (imported only
 * here); the view calls `buildJourney(agent, ctx)` and gets back a
 * `Phase[]` that the shared timeline / column / endpoint components
 * render uniformly.
 *
 * The switch is EXHAUSTIVE-typed against `Protocol` so a new addition
 * produces a typecheck failure at every branch until the builder is
 * wired.
 */

import type { Protocol } from '../roster.js';

import { buildAppleJourney } from './apple.js';
import { buildChatGptJourney } from './chatgpt.js';
import { buildClaudeJourney } from './claude.js';
import { buildCopilotJourney } from './copilot.js';
import { buildCustomJourney } from './custom.js';
import { buildDuckDuckGoJourney } from './duckduckgo.js';
import { buildGeminiJourney } from './gemini.js';
import { buildGrokJourney } from './grok.js';
import { buildMetaJourney } from './meta.js';
import { buildMistralJourney } from './mistral.js';
import { buildPerplexityJourney } from './perplexity.js';
import { buildUnverifiedJourney } from './unverified.js';
import type { JourneyContext, Phase } from './types.js';

/**
 * TypeScript exhaustiveness helper. If a new `Protocol` case is added
 * and any of the per-agent dispatchers below misses it, the assignment
 * to `never` fails at compile time.
 */
function assertNever(_x: never): never {
  throw new Error('Unhandled protocol dispatch');
}

/**
 * Per-agent-id dispatch. `apple` / `duckduckgo` / `meta` / `mistral` all
 * share the `discovery-only` protocol but render agent-specific evidence
 * captions in the phase narration, so we key on `agent.id` here (not
 * `agent.protocol`) for `discovery-only`. The other protocols are
 * one-to-one with a single agent so the switch is clean.
 */
export function buildJourney(ctx: JourneyContext): Phase[] {
  const { agent } = ctx;
  const protocol: Protocol = agent.protocol;
  switch (protocol) {
    case 'mcp':
      return buildClaudeJourney(ctx);
    case 'acp':
      return buildChatGptJourney(ctx);
    case 'a2a-ap2':
      return buildGeminiJourney(ctx);
    case 'transact-pilot':
      return agent.id === 'copilot'
        ? buildCopilotJourney(ctx)
        : buildPerplexityJourney(ctx);
    case 'discovery-only':
      switch (agent.id) {
        case 'meta':
          return buildMetaJourney(ctx);
        case 'mistral':
          return buildMistralJourney(ctx);
        case 'apple':
          return buildAppleJourney(ctx);
        case 'duckduckgo':
          return buildDuckDuckGoJourney(ctx);
        // Fallback for any future `discovery-only` addition — uses the
        // Meta shape (the terminal-phase renderer is agent-agnostic).
        default:
          return buildMetaJourney(ctx);
      }
    case 'unidentifiable':
      return buildGrokJourney(ctx);
    case 'blocked':
      return buildUnverifiedJourney(ctx);
    case 'custom':
      return buildCustomJourney(ctx);
    default:
      return assertNever(protocol);
  }
}
